import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, Clock, Upload, X, MessageSquare, Users, Check, ChevronDown, ChevronUp, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useImageUpload } from '@/hooks/useImageUpload';
import { ServiceRequestUrgency } from '@/types/serviceRequest';
import { supabase } from '@/integrations/supabase/client';
import { convertFilesToUploadFormat, validateFilesForUpload } from '@/utils/fileConversion';
import { useCommunity } from '@/contexts/CommunityContext';
import { PATH } from '@/routes/routeConfig';

interface Business {
  id: string;
  name: string;
  company_logo?: string | null;
  company_banner_url?: string | null;
  city?: string;
  postal_code?: string;
}

interface ReachOutModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBusinesses: Business[];
  searchParams: Record<string, unknown>;
  onSuccess: (result: { serviceRequestId: string; conversationId?: string }) => void;
  requiresServiceDescription?: boolean;
  initialServiceDescription?: string;
}

interface BusinessComment {
  businessId: string;
  comment: string;
}

export const ReachOutModal: React.FC<ReachOutModalProps> = ({
  isOpen,
  onClose,
  selectedBusinesses,
  searchParams,
  onSuccess,
  requiresServiceDescription = false,
  initialServiceDescription = ''
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentCommunity } = useCommunity();
  const navigate = useNavigate();
  
  // Form state - Initialize urgency from searchParams if available
  const [urgency, setUrgency] = useState<ServiceRequestUrgency>(
    (searchParams?.urgency as ServiceRequestUrgency) || 'in_the_week'
  );
  const [serviceDescription, setServiceDescription] = useState(initialServiceDescription);
  const [showMessage, setShowMessage] = useState(false);
  const [globalComment, setGlobalComment] = useState('');
  const [usePerBusinessComments, setUsePerBusinessComments] = useState(false);
  const [businessComments, setBusinessComments] = useState<BusinessComment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  
  // Image upload hook
  const {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    hasImages
  } = useImageUpload();

  // Initialize business comments when switching to per-business mode
  const handleTogglePerBusinessComments = (checked: boolean) => {
    setUsePerBusinessComments(checked);
    if (checked && businessComments.length === 0) {
      setBusinessComments(
        selectedBusinesses.map(business => ({
          businessId: business.id,
          comment: globalComment // Pre-fill with global comment
        }))
      );
    }
  };

  // Update individual business comment
  const updateBusinessComment = (businessId: string, comment: string) => {
    setBusinessComments(prev => 
      prev.map(bc => 
        bc.businessId === businessId 
          ? { ...bc, comment }
          : bc
      )
    );
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      addImages(files);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate service description if required
    if (requiresServiceDescription && !serviceDescription.trim()) {
      toast({
        title: t('common.error'),
        description: t('reach_out_modal.error.description_required'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert and validate attachments if any
      let attachments = undefined;
      if (hasImages) {
        const files = pendingImages.map(img => img.file);
        
        // Validate files first
        const validation = validateFilesForUpload(files);
        if (!validation.isValid) {
          toast({
            title: t('common.error'),
            description: validation.error,
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
        
        // Convert files to upload format
        try {
          attachments = await convertFilesToUploadFormat(files);
        } catch (error) {
          toast({
            title: t('common.error'),
            description: error instanceof Error ? error.message : t('reach_out_modal.error.attachments_failed'),
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Prepare the request payload
      const updatedSearchParams = requiresServiceDescription && serviceDescription.trim() 
        ? { ...searchParams, originalRequest: serviceDescription.trim(), communityId: currentCommunity?.id }
        : { ...searchParams, communityId: currentCommunity?.id };
        
      const payload = {
        searchParams: updatedSearchParams,
        selectedBusinessIds: selectedBusinesses.map(b => b.id),
        globalComment: (showMessage && !usePerBusinessComments && globalComment.trim()) ? globalComment.trim() : undefined,
        businessComments: (showMessage && usePerBusinessComments) ? businessComments.filter(bc => bc.comment.trim()) : undefined,
        urgency,
        attachments
      };

      console.log('Submitting reach out request:', payload);

      // Call request-service edge function
      const { data: result, error } = await supabase.functions.invoke('request-service', {
        body: payload
      });

      if (error) {
        throw new Error(error.message || 'Failed to create service request');
      }

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create service request');
      }

      console.log('Service request created successfully:', result);

      // Clear form and close modal
      setServiceDescription('');
      setShowMessage(false);
      setGlobalComment('');
      setBusinessComments([]);
      setUsePerBusinessComments(false);
      setShowAttachments(false);
      clearImages();
      onClose();

      // Show success message
      toast({
        title: t('common.success'),
        description: t('reach_out_modal.success.message', { 
          count: selectedBusinesses.length,
          business: selectedBusinesses.length === 1 ? t('common.business') : t('common.businesses')
        }),
      });

      // Navigate to service requests page (same logic as ServiceRequest page)
      navigate(PATH.MY_SERVICE_REQUESTS);

      // Trigger success callback
      onSuccess({
        serviceRequestId: result.serviceRequestId,
        conversationId: result.conversationId
      });

    } catch (error) {
      console.error('Error submitting reach out request:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : t('reach_out_modal.error.send_failed'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <ArrowRightLeft className="w-6 h-6 text-primary" />
            {t('reach_out_modal.title', { count: selectedBusinesses.length })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Selected Businesses Preview */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Selected Businesses:</h3>
            <div className="flex flex-wrap gap-2">
              {selectedBusinesses.map((business) => (
                <div key={business.id} className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1 text-sm">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary" />
                  </div>
                  {business.name}
                </div>
              ))}
            </div>
          </div>

          {/* Service Request Description - when required */}
          {requiresServiceDescription && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t('reach_out_modal.service_description.label')} *
              </label>
              <Textarea
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
                placeholder={t('reach_out_modal.service_description.placeholder')}
                className="min-h-[120px] resize-none"
                disabled={isSubmitting}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Provide a detailed description of your service request.
              </p>
            </div>
          )}

          {/* Urgency Selection */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4" />
              {t('reach_out_modal.urgency.label')}
            </label>
            <Select value={urgency} onValueChange={(value: ServiceRequestUrgency) => setUrgency(value)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="urgently">{t('reach_out_modal.urgency.options.urgently')}</SelectItem>
                <SelectItem value="in_the_day">{t('reach_out_modal.urgency.options.in_the_day')}</SelectItem>
                <SelectItem value="tomorrow">{t('reach_out_modal.urgency.options.tomorrow')}</SelectItem>
                <SelectItem value="in_the_week">{t('reach_out_modal.urgency.options.in_the_week')}</SelectItem>
                <SelectItem value="in_the_month">{t('reach_out_modal.urgency.options.in_the_month')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Message Section - Collapsible - Hidden when requiresServiceDescription is true */}
          {!requiresServiceDescription && (
            <div className="border rounded-lg p-4">
              <button
                type="button"
                onClick={() => setShowMessage(!showMessage)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {t('reach_out_modal.comments.label')}
                    {showMessage && (
                      <span className="ml-2 text-primary">
                        ({usePerBusinessComments ? t('reach_out_modal.comments.per_business') : t('reach_out_modal.comments.global')})
                      </span>
                    )}
                  </span>
                </div>
                {showMessage ? (
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                )}
              </button>
            
            {showMessage && (
              <div className="mt-4">
                {!usePerBusinessComments ? (
                  <div>
                    <Textarea
                      value={globalComment}
                      onChange={(e) => setGlobalComment(e.target.value)}
                      placeholder="Describe what you need and any specific requirements..."
                      className="min-h-[120px] resize-none"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This message will be sent to all {selectedBusinesses.length} selected businesses.
                    </p>
                    {selectedBusinesses.length > 1 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Switch
                          id="per-business-toggle"
                          checked={usePerBusinessComments}
                          onCheckedChange={handleTogglePerBusinessComments}
                          className="h-4 w-8"
                        />
                        <label
                          htmlFor="per-business-toggle"
                          className="text-xs text-gray-600 cursor-pointer"
                        >
                          Add additional details for each business
                        </label>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="space-y-4">
                      {selectedBusinesses.map((business) => {
                        const businessComment = businessComments.find(bc => bc.businessId === business.id);
                        return (
                          <div key={business.id} className="border rounded-lg p-4">
                            <div className="flex items-center gap-3 mb-3">
                              {business.company_logo && (
                                <img 
                                  src={business.company_logo} 
                                  alt={business.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              )}
                              <div>
                                <h4 className="font-medium">{business.name}</h4>
                                {(business.city || business.postal_code) && (
                                  <p className="text-xs text-gray-500">
                                    {[business.city, business.postal_code].filter(Boolean).join(', ')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Textarea
                              value={businessComment?.comment || ''}
                              onChange={(e) => updateBusinessComment(business.id, e.target.value)}
                              placeholder={`Specific message for ${business.name}...`}
                              className="min-h-[80px] resize-none"
                              disabled={isSubmitting}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          )}

          {/* Image Attachments - Collapsible */}
          <div className="border rounded-lg p-4">
            <button
              type="button"
              onClick={() => setShowAttachments(!showAttachments)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  Attachments (optional)
                  {hasImages && <span className="ml-2 text-primary">({pendingImages.length} added)</span>}
                </span>
              </div>
              {showAttachments ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>
            
            {showAttachments && (
              <div className="mt-4">
                {/* Upload Zone */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drop images here or click to select
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    disabled={isSubmitting}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('file-upload')?.click()}
                    disabled={isSubmitting}
                  >
                    Choose Files
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum 3 images, up to 10MB each
                  </p>
                </div>

                {/* Image Previews */}
                {hasImages && (
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    {pendingImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image.preview}
                          alt={`Attachment ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={isSubmitting}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                {t('reach_out_modal.button.send_request', { count: selectedBusinesses.length })}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};